import React, { useEffect, useMemo } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useI18n } from '../i18n/context';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { LegalParagraphs } from './LegalText';

/**
 * Публичная страница политики конфиденциальности.
 *
 * Доступна без логина по `/privacy`. Используется как:
 *  - URL для Google Play Console / App Store Connect (поле «Privacy policy URL»);
 *  - ссылка из формы регистрации (UserSelector) и приветственного экрана;
 *  - in-app экран на мобильных (Capacitor) — встроенный, чтобы не открывать браузер.
 */
export default function PrivacyPolicy({ onBack }) {
  const { t } = useI18n();

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const prev = document.title;
      document.title = `${t('privacy.title')} · MadCoach`;
      return () => {
        document.title = prev;
      };
    }
    return undefined;
  }, [t]);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (typeof window !== 'undefined') {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.assign('/');
      }
    }
  };

  const summaryBullets = useMemo(() => {
    const keys = [];
    for (let i = 1; i <= 9; i += 1) {
      keys.push(`privacy.sum${i}`);
    }
    return keys.map((key) => t(key)).filter((text) => text.trim().length > 0);
  }, [t]);

  const sections = useMemo(
    () =>
      Array.from({ length: 11 }, (_, i) => {
        const n = i + 1;
        return {
          title: t(`privacy.s${n}.title`),
          body: t(`privacy.s${n}.body`),
        };
      }),
    [t],
  );

  return (
    <div className="relative min-h-[100dvh] min-h-screen w-full bg-gradient-to-b from-slate-50 to-white">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-4 py-3 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleBack}
            aria-label={t('privacy.back')}
            className="h-9 w-9 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex min-w-0 items-center gap-2">
            <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
            <span className="truncate text-sm font-medium text-slate-700">
              {t('privacy.title')}
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 pb-20 pt-8 sm:px-6">
        {/* Hero */}
        <div className="mb-8">
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" />
            MadCoach
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {t('privacy.title')}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t('privacy.lastUpdated')}</p>

          <div className="mt-8 rounded-xl border border-slate-200/80 bg-white/90 p-5 shadow-sm ring-1 ring-slate-100">
            <h2 className="text-base font-semibold text-slate-900">{t('privacy.sumTitle')}</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-slate-700">{t('privacy.sumIntro')}</p>
            <ul className="mt-4 list-none space-y-3 pl-0 text-[15px] leading-relaxed text-slate-700">
              {summaryBullets.map((item, idx) => (
                <li key={`privacy-sum-${idx}`} className="flex gap-2.5">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 border-t border-slate-100 pt-4 text-sm leading-relaxed text-slate-500">
              {t('privacy.sumDisclaimer')}
            </p>
          </div>
        </div>

        <Separator className="bg-slate-200" />

        {/* Sections */}
        <div className="mt-10 space-y-8">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="mb-3 text-base font-semibold text-slate-900 sm:text-lg">{s.title}</h2>
              <LegalParagraphs text={s.body} />
            </section>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button type="button" variant="outline" onClick={handleBack} className="min-w-32">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('privacy.back')}
          </Button>
        </div>
      </main>
    </div>
  );
}
